import type { ExternalGameLink } from "../app/rosebud-catalog";
import "./external-overlay.css";

export interface ExternalOverlayHooks {
  onRequestClose(): void;
}

/**
 * Frames a Rosebud game on Carcade's own document instead of navigating
 * away. The wrapper is fixed/inset:0 (external-overlay.css) so it already
 * fills the browser window on its own; it deliberately does not call
 * requestFullscreen(), which would hijack the whole OS display on a
 * non-touch desktop while Rosebud's own page chrome still framed the game
 * underneath, gaining nothing. When the catalog entry has a harvested
 * gatewayUrl (its own Rosebud game-server origin, scraped at catalog-build
 * time), that's framed directly for a genuinely chrome-free game; otherwise
 * this falls back to the ordinary rosebud.ai play page.
 */
/**
 * Below this, a load is presumed to just be a normal cold start; at and
 * beyond it, the loading indicator switches to a "still working on this"
 * state so a slow load doesn't read identically to a dead one. Not an
 * error/timeout -- the iframe keeps loading either way, this only changes
 * what's shown while it does.
 */
const STALL_MS = 6000;

export class ExternalOverlay {
  readonly #hooks: ExternalOverlayHooks;
  readonly #document: Document;
  #wrapper: HTMLElement | null = null;
  #stallTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(hooks: ExternalOverlayHooks, documentScope: Document = document) {
    this.#hooks = hooks;
    this.#document = documentScope;
  }

  isOpen(): boolean {
    return this.#wrapper !== null;
  }

  open(link: ExternalGameLink): void {
    this.close();

    const wrapper = this.#document.createElement("div");
    wrapper.className = "external-overlay";
    wrapper.dataset.externalOverlay = "open";
    wrapper.dataset.externalGameSlug = link.slug;

    const iframe = this.#document.createElement("iframe");
    iframe.className = "external-overlay__frame";
    iframe.src = link.gatewayUrl ?? link.url;
    iframe.title = link.title;
    iframe.setAttribute(
      "allow",
      "fullscreen; autoplay; gamepad; screen-wake-lock; picture-in-picture",
    );
    iframe.setAttribute("allowfullscreen", "true");

    // Zero feedback while a third-party game loads used to mean a slow
    // cold start and a genuinely dead load looked identical: a black
    // screen either way. This is symbol-only (a spinner, no prose),
    // consistent with the rest of the in-game chrome.
    const loading = this.#document.createElement("div");
    loading.className = "external-overlay__loading";
    loading.dataset.externalOverlayLoading = "starting";
    loading.setAttribute("role", "status");
    loading.setAttribute("aria-label", "Loading");
    const spinner = this.#document.createElement("div");
    spinner.className = "external-overlay__spinner";
    loading.append(spinner);

    const clearStallTimer = (): void => {
      if (this.#stallTimer === null) return;
      clearTimeout(this.#stallTimer);
      this.#stallTimer = null;
    };
    iframe.addEventListener("load", () => {
      clearStallTimer();
      loading.remove();
    });
    this.#stallTimer = setTimeout(() => {
      this.#stallTimer = null;
      loading.dataset.externalOverlayLoading = "stalled";
    }, STALL_MS);

    const close = this.#document.createElement("button");
    close.type = "button";
    close.className = "external-overlay__close";
    close.setAttribute("aria-label", `Close ${link.title}`);
    close.textContent = "×";
    close.addEventListener("click", () => this.#hooks.onRequestClose());

    wrapper.append(iframe, loading, close);
    this.#document.body.append(wrapper);
    this.#wrapper = wrapper;
  }

  close(): void {
    const wrapper = this.#wrapper;
    if (this.#stallTimer !== null) {
      clearTimeout(this.#stallTimer);
      this.#stallTimer = null;
    }
    if (!wrapper) return;
    this.#wrapper = null;
    const iframe = wrapper.querySelector("iframe");
    if (iframe) iframe.src = "about:blank"; // stop game audio/execution before teardown
    wrapper.remove();
  }
}
